import {
    Box,
    Flex,
    IconButton,
    Popover,
    PopoverCloseButton,
    PopoverContent,
    PopoverTrigger,
    Text,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { fetchMenuStructure } from '../../../app-state/slices/AppDataSlice';
import { logout, setSwitchUser } from '../../../app-state/slices/AuthSlice';
import { resetPortfolioClientSelection } from '../../../app-state/slices/ClientsSlice';
import { resetGeneratedReports } from '../../../app-state/slices/ReportsSlice';
import { AppDispatch, RootState } from '../../../app-state/store';
import apiClient from '../../api';
import * as ga from '../../google-analytics';
import { useCurrentColors } from '../../hooks/UseCurrentColors/UseCurrentColors';
import {
    useCheckForMobile,
    useGetDeviceType,
} from '../../hooks/UseResponsive/UseResponsive';
import CanopyIconButton from '../IconButton/IconButton';
import { USER_PROFILE } from './NavMenuItem';

function UserProfile({ t, name, path, icon }: any) {
    const router = useNavigate();
    const deviceType = useGetDeviceType();
    const currentColors = useCurrentColors();
    const {
        auth: { user, switchUser },
        appData: {
            menuStructure: {
                data: { navBar },
            },
        },
    } = useSelector((state: RootState) => state);
    const isMobile = useCheckForMobile();
    const dispatch = useDispatch<AppDispatch>();

    const onSwitchUser = async () => {
        ga.event({
            action: `User Profile > SA Switch User `,
            user: apiClient.session,
        });
        dispatch(setSwitchUser(null));
        apiClient.clearSwitchedUser();
        dispatch(resetPortfolioClientSelection());
        dispatch(resetGeneratedReports());
        dispatch(fetchMenuStructure({ deviceType }));
        router('/login');
    };

    const onLogout = async () => {
        dispatch(logout());
    };

    const subMenus: Record<string, any> = useMemo(() => {
        const submenu: any[] =
            navBar.find((n) => n.key === USER_PROFILE)?.subMenu || [];
        let menus: Record<string, any> = {};
        submenu?.forEach((item: any) => {
            menus[item.key] = item;
        });
        return menus;
    }, [navBar]);

    return (
        <Popover placement="bottom">
            {
                <PopoverTrigger>
                    <IconButton
                        variant="navbar"
                        aria-label={name}
                        icon={icon}
                        ml="15px"
                        padding="10px"
                    />
                </PopoverTrigger>
            }
            <PopoverContent
                backgroundColor={currentColors.componentBg}
                boxShadow={`0px 0px 6px ${currentColors.boxShadow}`}
                position="relative"
                top="0px"
                width="325px"
                right="40px"
                _focus={{
                    border: '0px',
                    outline: 'none',
                }}>
                {isMobile && <PopoverCloseButton />}
                <Box p="8px 16px">
                    <Flex
                        pt="10px"
                        pb="20px"
                        alignItems="center"
                        justify="space-between">
                        <Box>
                            <Text
                                fontSize="15px"
                                fontWeight="bold"
                                color={currentColors.contentText}>
                                {user?.display_name}
                            </Text>
                            <Text
                                fontSize="13px"
                                fontWeight="medium"
                                color={currentColors.brand['300']}>
                                {user?.username}
                            </Text>
                        </Box>
                    </Flex>

                    {switchUser && (
                        <Flex
                            py="16px"
                            borderTop="1px solid #eee"
                            direction="column">
                            <Text
                                pb="8px"
                                fontSize="15px"
                                fontWeight="bold"
                                color={currentColors.contentText}>
                                {t('currently-viewing')}
                            </Text>
                            <Flex justify="space-between">
                                <Box>
                                    <Text
                                        fontSize="15px"
                                        fontWeight="bold"
                                        color={currentColors.contentText}>
                                        {switchUser?.display_name}
                                    </Text>
                                    <Text
                                        fontSize="13px"
                                        fontWeight="medium"
                                        color={currentColors.brand['300']}>
                                        {switchUser?.username}
                                    </Text>
                                </Box>

                                <CanopyIconButton
                                    background="transparent"
                                    cursor="pointer"
                                    border="none"
                                    _hover={{
                                        backgroundColor: 'transparent',
                                    }}
                                    variant="ghost"
                                    width="20px"
                                    height="20px"
                                    mx="8px"
                                    color={currentColors.primaryColor}
                                    bg="transparent"
                                    aria-label={t('ui.mm.switch-to-sa')}
                                    icon="NavClient"
                                    onClick={onSwitchUser}></CanopyIconButton>
                            </Flex>
                        </Flex>
                    )}
                    {subMenus?.['settings'] ? (
                        <Flex
                            key={subMenus?.['settings']?.key}
                            py="16px"
                            justify="space-between"
                            cursor="pointer"
                            borderTop="1px solid #eee">
                            <Link to={subMenus?.['settings']?.path || ''}>
                                <Text
                                    fontSize="14px"
                                    _hover={{
                                        color: currentColors.whiteAlpha['200'],
                                    }}
                                    color={currentColors.whiteAlpha['400']}>
                                    {t(`ui.mm.profile-menu-settings`)}
                                </Text>
                            </Link>
                        </Flex>
                    ) : null}

                    {subMenus?.['logout'] ? (
                        <Flex
                            py="16px"
                            justify="space-between"
                            borderTop="1px solid #eee">
                            <Text
                                fontSize="14px"
                                color={currentColors.contentText}>
                                {t('logout')}
                            </Text>
                            <CanopyIconButton
                                background="transparent"
                                cursor="pointer"
                                border="none"
                                aria-label="logout"
                                _hover={{ backgroundColor: 'transparent' }}
                                variant="ghost"
                                icon="Logout"
                                width="20px"
                                height="20px"
                                mx="8px"
                                color={currentColors.contentText}
                                onClick={() => {
                                    ga.event({
                                        action: `User Profile > Logout`,
                                        user: apiClient.session,
                                    });
                                    onLogout();
                                }}
                            />
                        </Flex>
                    ) : null}
                </Box>
            </PopoverContent>
        </Popover>
    );
}
export default UserProfile;
